import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  LOAD_UNIT_CHECKIN_PROFILE,
  INSERT_UNIT_CHECKIN_PROFILE,
  UNIT_CHECKIN_PROFILE_ERROR,
  PROCESS_NEW_UNIT_CHECKIN_PROFILE,
  insertUnitCheckinProfile,
  PROCESS_UNIT_CHECKIN_PROFILE,
  UNIT_CHECKIN_PROFILE_CREATE_ERROR,
  EDIT_UNIT_CHECKIN_PROFILE,
  CREATE_NEW_UNIT_CHECKIN_PROFILE,
} from '../actions/unitCheckinProfileActions';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  wasUpdatedText,
  wasNotCreatedText,
  wasCreatedText,
  wasNotLoadedText,
} from '../../components/shared/SharedInternationalizations';
import { messageTypes } from '../../components/ui/AlertMessage';
import { errorDisplay, redirect } from '../../utilities/general';

export const checkinProfileText = (
  <FormattedMessage
    id="unitMiddleware.checkinProfileText"
    defaultMessage="Unit Check-In Preferences Profile For"
    description="The staff preferences for a unit"
  />
);

/**
 * Middleware to trigger an api call to retrieve all of the units
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the units
 */
export const loadUnitCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_UNIT_CHECKIN_PROFILE) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unitcheckinprofile +
        action.payload,
      onSuccess: INSERT_UNIT_CHECKIN_PROFILE,
      onError: '',
      auth: store.getState().auth.token,
      body: false,
    };
    // store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated unit preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers replacing the current unit information with the updated unit information
 * @fires history.push - Tells react-router to switch back to the normal view of the unit from the edit view
 * @fires dispatch#unitMsgEditSuccess - Triggers showing the unit edit success message
 * @fires dispatch#clearUnitMsg - Triggers hiding of the unit message
 */
export const processNewUnitCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_NEW_UNIT_CHECKIN_PROFILE) {
    store.dispatch(hideLoading());
    store.dispatch(insertUnitCheckinProfile(action.payload));

    const unitObj = store.getState().units.units[action.payload.unit];

    let url = '/units/' + action.payload.unit + '/view/';
    redirect(action, url);

    const messageText = (
      <React.Fragment>
        {checkinProfileText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{unitObj.name}"
        </span>
        {wasCreatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the updated unit preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers replacing the current unit information with the updated unit information
 * @fires history.push - Tells react-router to switch back to the normal view of the unit from the edit view
 * @fires dispatch#unitMsgEditSuccess - Triggers showing the unit edit success message
 * @fires dispatch#clearUnitMsg - Triggers hiding of the unit message
 */
export const processUnitCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_UNIT_CHECKIN_PROFILE) {
    store.dispatch(hideLoading());
    store.dispatch(insertUnitCheckinProfile(action.payload));

    const unitObj = store.getState().units.units[action.payload.unit];

    let url = '/units/' + action.payload.unit + '/view/';
    redirect(action, url);

    const messageText = (
      <React.Fragment>
        {checkinProfileText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{unitObj.name}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#unitMsgArchiveError - Triggers showing the unit archive error message
 * @fires dispatch#clearUnitMsg - Triggers hiding of the unit message
 */
export const unitCheckinProfileCreateError = (store) => (next) => (action) => {
  next(action);
  if (action.type === UNIT_CHECKIN_PROFILE_CREATE_ERROR) {
    store.dispatch(hideLoading());
    const unitObj = store.getState().units.units[action.originalPayload.unit];
    const messageText = (
      <React.Fragment>
        {checkinProfileText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{unitObj.name}"
        </span>
        {wasNotCreatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the units
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the units
 */
export const addUnitCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === CREATE_NEW_UNIT_CHECKIN_PROFILE) {
    const body = {
      ...action.payload,
      checkin_device: action.payload.checkin_device
        ? parseInt(action.payload.checkin_device)
        : null,
    };
    const meta = {
      method: 'POST',
      url:
        apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].unitcheckinprofile,
      onSuccess: PROCESS_UNIT_CHECKIN_PROFILE,
      onError: UNIT_CHECKIN_PROFILE_CREATE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to trigger an api call to retrieve all of the units
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the units
 */
export const editUnitCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_UNIT_CHECKIN_PROFILE) {
    const body = {
      ...action.payload,
      checkin_device: action.payload.checkin_device
        ? parseInt(action.payload.checkin_device)
        : null,
    };
    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unitcheckinprofile +
        action.payload.unit +
        '/',
      onSuccess: PROCESS_UNIT_CHECKIN_PROFILE,
      onError: UNIT_CHECKIN_PROFILE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#unitMsgArchiveError - Triggers showing the unit archive error message
 * @fires dispatch#clearUnitMsg - Triggers hiding of the unit message
 */
export const unitCheckinProfileError = (store) => (next) => (action) => {
  next(action);
  if (action.type === UNIT_CHECKIN_PROFILE_ERROR) {
    store.dispatch(hideLoading());
    const unitObj = store.getState().units.units[action.originalPayload.unit];
    const messageText = (
      <React.Fragment>
        {checkinProfileText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{unitObj.name}"
        </span>
        {wasNotLoadedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    store.dispatch(addUiMsg(message));
  }
};

export const unitCheckinProfileMdl = [
  loadUnitCheckinProfile,
  processNewUnitCheckinProfile,
  unitCheckinProfileError,
  unitCheckinProfileCreateError,
  processUnitCheckinProfile,
  editUnitCheckinProfile,
  addUnitCheckinProfile,
];
